import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canAny } from '../../api/client';
import {
  createBaseline,
  createProject,
  deleteProject,
  fetchBaselines,
  fetchProjects,
  updateProject,
} from '../../api/ops';
import { deviceStatic, useStaticVersion } from '../../realtime/deviceStatic';
import { Card, Field, Modal } from '../../ui/components';
import { formatDateTime, formatNumber } from '../../ui/format';
import { useMe } from '../auth/session';
import { useReportScope } from './ReportScope';
import { SerialListModal } from './SerialList';

/** Quiet time after the last registry change before the projects are read again (adoptions come in bursts at dusk). */
const MEMBERSHIP_SETTLE_MS = 2000;

/**
 * Re-reads the projects (devices / not installed) and their serial lists while the page is open, when the devices'
 * project membership changes in the realtime registry (a listed device installed, moved, released). Only these
 * queries, only on an actual membership change, and only once a burst of registry events has settled.
 */
export function useProjectMembershipRefresh() {
  const qc = useQueryClient();
  const version = useStaticVersion((s) => s.version);
  const membership = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of deviceStatic.values()) if (d.projectId) m.set(d.projectId, (m.get(d.projectId) ?? 0) + 1);
    return [...m].sort(([a], [b]) => a.localeCompare(b)).join(';');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the Map is mutable; version marks its changes
  }, [version]);
  const seen = useRef(membership);
  useEffect(() => {
    if (membership === seen.current) return;
    const timer = setTimeout(() => {
      seen.current = membership;
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['projectSerials'] });
    }, MEMBERSHIP_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [membership, qc]);
}

/** Projects of the client and their official energy baselines (AFM method; history kept). */
export function ProjectsTab() {
  useProjectMembershipRefresh();
  const { t } = useTranslation();
  const me = useMe().data;
  const { clientId } = useReportScope();
  const projects = useQuery({
    queryKey: ['projects', clientId],
    queryFn: () => fetchProjects(clientId),
    enabled: !!clientId,
  });
  const [editing, setEditing] = useState<api.Project | 'new' | null>(null);
  const [baselineFor, setBaselineFor] = useState<api.Project | null>(null);
  const [serialsOf, setSerialsOf] = useState<api.Project | null>(null);
  const manageAny = canAny(me, 'project.manage');
  return (
    <div className="ops-stack">
      <Card
        title={t('projects.title')}
        actions={
          manageAny && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setEditing('new')}
              disabled={!clientId}
            >
              <Plus size={15} /> {t('projects.new')}
            </button>
          )
        }
        bodyClass=""
      >
        <div className="card-body muted small" style={{ paddingBottom: 0 }}>
          {t('projects.hint')}
        </div>
        <div className="ops-scroll">
          <table className="table table-wrap">
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('projects.scope')}</th>
                <th>{t('projects.dates')}</th>
                <th>{t('projects.baseline')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(projects.data?.items ?? []).map((p) => (
                <tr key={p.id}>
                  <td>
                    <strong>{p.name}</strong>
                    {p.is_default && (
                      <span className="pill" style={{ marginLeft: 6 }} title={t('projects.defaultHint')}>
                        {t('projects.default')}
                      </span>
                    )}
                    <span className="cell-sub">
                      {[p.code, p.funding_programme, p.contract_number].filter(Boolean).join(' · ')}
                    </span>
                  </td>
                  <td>
                    {t('projects.devices', { n: formatNumber(p.devices) })}
                    {p.serials.not_installed > 0 && (
                      <span className="cell-sub">
                        <span className="pill pill-warning">
                          {t('serials.notInstalled', { n: formatNumber(p.serials.not_installed) })}
                        </span>
                      </span>
                    )}
                  </td>
                  <td className="nowrap">
                    {p.start_date ?? '—'} → {p.end_date ?? '—'}
                    <span className="cell-sub">{t(`projects.status.${p.status}`)}</span>
                  </td>
                  <td>
                    {p.baseline ? (
                      <>
                        {formatNumber(p.baseline.annual_value)} {p.baseline.unit}/{t('afm.year')}
                        <span className="cell-sub">
                          {p.baseline.source_document} · {p.baseline.reference_date}
                        </span>
                      </>
                    ) : (
                      <span className="pill pill-warning">{t('afm.noBaseline')}</span>
                    )}
                  </td>
                  <td className="right nowrap">
                    {(p.can_manage || p.serials.listed > 0) && (
                      <button type="button" className="btn btn-ghost" onClick={() => setSerialsOf(p)}>
                        {t('serials.list')}
                      </button>
                    )}
                    <button type="button" className="btn btn-ghost" onClick={() => setBaselineFor(p)}>
                      {t('projects.baselines')}
                    </button>
                    {p.can_manage && (
                      <button type="button" className="btn btn-ghost" onClick={() => setEditing(p)}>
                        {t('common.edit')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {projects.data?.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    {t('projects.none')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      {editing && clientId && (
        <ProjectModal
          clientId={clientId}
          project={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {baselineFor && <BaselinesModal project={baselineFor} onClose={() => setBaselineFor(null)} />}
      {serialsOf && <SerialListModal project={serialsOf} onClose={() => setSerialsOf(null)} />}
    </div>
  );
}

function ProjectModal({
  clientId,
  project,
  onClose,
}: {
  clientId: string;
  project: api.Project | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: project?.name ?? '',
    code: project?.code ?? '',
    funding_programme:
      project?.funding_programme ??
      'AFM — Programul privind creșterea eficienței energetice în infrastructura de iluminat public',
    contract_number: project?.contract_number ?? '',
    description: project?.description ?? '',
    start_date: project?.start_date ?? '',
    end_date: project?.end_date ?? '',
    status: project?.status ?? 'active',
  });
  const set = (p: Partial<typeof f>) => setF({ ...f, ...p });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name,
        code: f.code || null,
        funding_programme: f.funding_programme || null,
        contract_number: f.contract_number || null,
        description: f.description || null,
        start_date: f.start_date || null,
        end_date: f.end_date || null,
        status: f.status as api.Project['status'],
      };
      return project
        ? updateProject(project.id, body)
        : createProject({
            client_id: clientId,
            ...body,
            code: body.code ?? undefined,
            funding_programme: body.funding_programme ?? undefined,
            contract_number: body.contract_number ?? undefined,
            description: body.description ?? undefined,
            start_date: body.start_date ?? undefined,
            end_date: body.end_date ?? undefined,
          });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
  });
  const del = useMutation({
    mutationFn: () => deleteProject(project!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
  });
  return (
    <Modal title={project ? project.name : t('projects.new')} onClose={onClose}>
      <div className="form-grid">
        <Field label={t('common.name')}>
          <input
            className="input"
            value={f.name}
            onChange={(e) => set({ name: e.target.value })}
            maxLength={200}
          />
        </Field>
        <Field label={t('projects.code')}>
          <input
            className="input"
            value={f.code}
            onChange={(e) => set({ code: e.target.value })}
            maxLength={64}
          />
        </Field>
        <Field label={t('projects.funding')}>
          <input
            className="input"
            value={f.funding_programme}
            onChange={(e) => set({ funding_programme: e.target.value })}
            maxLength={300}
          />
        </Field>
        <Field label={t('projects.contract')}>
          <input
            className="input"
            value={f.contract_number}
            onChange={(e) => set({ contract_number: e.target.value })}
            maxLength={100}
          />
        </Field>
        <Field label={t('projects.start')}>
          <input
            className="input"
            type="date"
            value={f.start_date}
            onChange={(e) => set({ start_date: e.target.value })}
          />
        </Field>
        <Field label={t('projects.end')} hint={t('projects.endHint')}>
          <input
            className="input"
            type="date"
            value={f.end_date}
            onChange={(e) => set({ end_date: e.target.value })}
          />
        </Field>
        <Field label={t('common.status')}>
          <select
            className="input"
            value={f.status}
            onChange={(e) => set({ status: e.target.value as api.Project['status'] })}
          >
            {(['planned', 'active', 'monitoring', 'closed'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`projects.status.${s}`)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={t('projects.description')}>
        <textarea
          className="input textarea"
          value={f.description}
          onChange={(e) => set({ description: e.target.value })}
          rows={3}
        />
      </Field>
      {(save.error ?? del.error) && (
        <p className="form-error">{String((save.error ?? del.error) as Error)}</p>
      )}
      <div className="form-actions">
        {project && !project.is_default && project.devices === 0 && (
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => del.mutate()}
            disabled={del.isPending}
          >
            {t('common.delete')}
          </button>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!f.name.trim() || save.isPending}
          onClick={() => save.mutate()}
        >
          {t('common.save')}
        </button>
      </div>
    </Modal>
  );
}

function BaselinesModal({ project, onClose }: { project: api.Project; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const history = useQuery({
    queryKey: ['baselines', project.id],
    queryFn: () => fetchBaselines(project.id),
  });
  const [f, setF] = useState({
    annual_value: '',
    unit: 'kWh' as 'kWh' | 'MWh',
    source_document: '',
    reference_date: '',
    notes: '',
    installed_power_before_kw: '',
    operating_hours_per_year: '4150',
    projected_annual_value: '',
    co2_factor_kg_per_kwh: '0.265',
    min_savings_pct: '25',
  });
  const set = (p: Partial<typeof f>) => setF({ ...f, ...p });
  const num = (s: string) => (s.trim() === '' ? undefined : Number(s.replace(',', '.')));
  const save = useMutation({
    mutationFn: () =>
      createBaseline(project.id, {
        annual_value: num(f.annual_value)!,
        unit: f.unit,
        source_document: f.source_document,
        reference_date: f.reference_date,
        ...(f.notes ? { notes: f.notes } : {}),
        ...(num(f.installed_power_before_kw) !== undefined
          ? { installed_power_before_kw: num(f.installed_power_before_kw) }
          : {}),
        ...(num(f.operating_hours_per_year) !== undefined
          ? { operating_hours_per_year: num(f.operating_hours_per_year) }
          : {}),
        ...(num(f.projected_annual_value) !== undefined
          ? { projected_annual_value: num(f.projected_annual_value) }
          : {}),
        ...(num(f.co2_factor_kg_per_kwh) !== undefined
          ? { co2_factor_kg_per_kwh: num(f.co2_factor_kg_per_kwh) }
          : {}),
        ...(num(f.min_savings_pct) !== undefined ? { min_savings_pct: num(f.min_savings_pct) } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['baselines', project.id] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
  const pie = num(f.installed_power_before_kw);
  const hours = num(f.operating_hours_per_year);
  return (
    <Modal title={t('projects.baselinesOf', { name: project.name })} onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('projects.baselineHint')}
      </p>
      {project.can_manage && (
        <Card title={t('projects.newBaseline')}>
          <div className="form-grid">
            <Field
              label={t('projects.annual')}
              hint={pie && hours ? t('projects.fromPie', { value: formatNumber(pie * hours) }) : undefined}
            >
              <input
                className="input"
                inputMode="decimal"
                value={f.annual_value}
                onChange={(e) => set({ annual_value: e.target.value })}
              />
            </Field>
            <Field label={t('projects.unit')}>
              <select
                className="input"
                value={f.unit}
                onChange={(e) => set({ unit: e.target.value as 'kWh' | 'MWh' })}
              >
                <option value="kWh">kWh</option>
                <option value="MWh">MWh</option>
              </select>
            </Field>
            <Field label={t('projects.document')}>
              <input
                className="input"
                value={f.source_document}
                onChange={(e) => set({ source_document: e.target.value })}
                maxLength={500}
              />
            </Field>
            <Field label={t('projects.referenceDate')}>
              <input
                className="input"
                type="date"
                value={f.reference_date}
                onChange={(e) => set({ reference_date: e.target.value })}
              />
            </Field>
            <Field label={t('projects.pie')}>
              <input
                className="input"
                inputMode="decimal"
                value={f.installed_power_before_kw}
                onChange={(e) => set({ installed_power_before_kw: e.target.value })}
              />
            </Field>
            <Field label={t('projects.hours')}>
              <input
                className="input"
                inputMode="decimal"
                value={f.operating_hours_per_year}
                onChange={(e) => set({ operating_hours_per_year: e.target.value })}
              />
            </Field>
            <Field label={t('projects.projected')}>
              <input
                className="input"
                inputMode="decimal"
                value={f.projected_annual_value}
                onChange={(e) => set({ projected_annual_value: e.target.value })}
              />
            </Field>
            <Field label={t('projects.co2')}>
              <input
                className="input"
                inputMode="decimal"
                value={f.co2_factor_kg_per_kwh}
                onChange={(e) => set({ co2_factor_kg_per_kwh: e.target.value })}
              />
            </Field>
            <Field label={t('projects.minSavings')}>
              <input
                className="input"
                inputMode="decimal"
                value={f.min_savings_pct}
                onChange={(e) => set({ min_savings_pct: e.target.value })}
              />
            </Field>
          </div>
          <Field label={t('projects.notes')}>
            <textarea
              className="input textarea"
              rows={2}
              value={f.notes}
              onChange={(e) => set({ notes: e.target.value })}
            />
          </Field>
          {save.isError && <p className="form-error">{t('errors.INVALID_REQUEST')}</p>}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={
                !num(f.annual_value) || !f.source_document.trim() || !f.reference_date || save.isPending
              }
              onClick={() => save.mutate()}
            >
              {t('projects.saveBaseline')}
            </button>
          </div>
        </Card>
      )}
      <h4>{t('projects.history')}</h4>
      <div className="ops-scroll">
        <table className="table table-tight">
          <thead>
            <tr>
              <th>{t('projects.annual')}</th>
              <th>{t('projects.document')}</th>
              <th>{t('projects.method')}</th>
              <th>{t('projects.recorded')}</th>
            </tr>
          </thead>
          <tbody>
            {(history.data?.items ?? []).map((b) => (
              <tr key={b.id} style={b.superseded_at ? { opacity: 0.6 } : undefined}>
                <td className="nowrap">
                  {formatNumber(b.annual_value)} {b.unit}
                  {!b.superseded_at && (
                    <span className="pill pill-online" style={{ marginLeft: 6 }}>
                      {t('projects.current')}
                    </span>
                  )}
                </td>
                <td>
                  {b.source_document}
                  <span className="cell-sub">
                    {b.reference_date}
                    {b.notes ? ` · ${b.notes}` : ''}
                  </span>
                </td>
                <td className="small">
                  {b.installed_power_before_kw
                    ? `Pie ${formatNumber(b.installed_power_before_kw, 2)} kW · `
                    : ''}
                  {formatNumber(b.operating_hours_per_year)} h · CO2 {b.co2_factor_kg_per_kwh} · min{' '}
                  {b.min_savings_pct} %
                  {b.projected_annual_value ? ` · Cf ${formatNumber(b.projected_annual_value)}` : ''}
                </td>
                <td className="muted small nowrap">
                  {formatDateTime(b.created_at)}
                  <span className="cell-sub">{b.created_by ?? '—'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
